// src/components/TicketViewsParameters.js
// Ticket views — each saved view becomes a tab on the dashboard's ticket
// card (after « Général », /dashboard?activeTab=view:<id>). The page shows
// that tab bar as it will look, then one row per view summarizing its
// filters with their real semantics (exclusions vs restrictions; empty
// restriction = everything), and a sectioned dialog to create or edit one.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Ban, Columns3, ExternalLink, Inbox, PanelsTopLeft, Pencil, Plus, Star, Tag, Trash2, UserRound,
} from 'lucide-react';
import { getStatusLabel } from '../constants/ticketStatuses';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, Modal, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import { formatShortDate } from '../utils/dates';
import { fetchPreferences } from '../utils/preferences';
import './TicketViewsParameters.css';

const NS = 'ticketListsUi';

const createEmptyFormState = () => ({
  name: '',
  description: '',
  statusesToExclude: [],
  resolutionActorsToExclude: [],
  topics: [],
  channelIds: [],
  employeeIds: []
});

const TicketViewsParameters = () => {
  const { t, i18n } = useTranslation();
  const tl = (key, fallback, options) => t(`${NS}.${key}`, fallback, options);
  const navigate = useNavigate();

  const [views, setViews] = useState(null); // null = loading
  const [submitting, setSubmitting] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);
  const [feedback, setFeedback] = useState(null); // {type, text}
  const [toDelete, setToDelete] = useState(null);
  // The signed-in person's own default view (GET /api/users/preferences).
  const [defaultViewId, setDefaultViewId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [formState, setFormState] = useState(() => createEmptyFormState());
  const [modalError, setModalError] = useState(null);

  const [options, setOptions] = useState(null); // null = loading
  const feedbackTimer = useRef(null);

  // A success leaves after 5 s; an error stays until closed.
  const showFeedback = useCallback((type, text) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, text });
    if (type === 'success') {
      feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
    }
  }, []);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchTicketViews = useCallback(async () => {
    if (!localStorage.getItem('access_token')) { navigate('/'); return; }
    try {
      const response = await axios.get('/api/tickets/ticket-views', { headers: authHeaders() });
      setViews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      setViews([]);
      showFeedback('error', err.response?.data?.detail || t('ticketViewsParameters.fetchError', 'Échec du chargement des vues de tickets.'));
    }
  }, [navigate, t, showFeedback]);

  const fetchTicketViewOptions = useCallback(async () => {
    try {
      const response = await axios.get('/api/tickets/ticket-views/options', { headers: authHeaders() });
      const data = response.data || {};
      setOptions({
        statuses: Array.isArray(data.statuses) ? data.statuses : [],
        resolutionActors: Array.isArray(data.resolution_actors) ? data.resolution_actors : [],
        topics: Array.isArray(data.topics) ? data.topics : [],
        channels: Array.isArray(data.channels) ? data.channels : [],
        employees: Array.isArray(data.employees) ? data.employees : []
      });
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      setOptions({ statuses: [], resolutionActors: [], topics: [], channels: [], employees: [] });
    }
  }, [navigate]);

  useEffect(() => {
    fetchTicketViews();
    fetchTicketViewOptions();
    let alive = true;
    fetchPreferences().then((prefs) => { if (alive) setDefaultViewId(prefs?.default_view_id || null); });
    return () => { alive = false; };
  }, [fetchTicketViews, fetchTicketViewOptions]);

  // Dashboard shows tabs oldest-first (backend sorts created_at asc) —
  // the list mirrors that order so it matches the tab bar users see.
  const orderedViews = useMemo(() => {
    return [...(views || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [views]);

  const getViewId = (view) => view.id || view._id;

  // « 28 oct. » — the shared short format (utils/dates: naive UTC, Paris time).
  const formatDate = (dateStr) => formatShortDate(dateStr, i18n.language, { empty: null });

  // ------------------------------------------------------------ dialog logic

  const openCreateModal = () => {
    setEditingView(null);
    setFormState(createEmptyFormState());
    setModalError(null);
    setShowModal(true);
  };

  const openEditModal = (view) => {
    const filters = view.filters || {};
    setEditingView(view);
    setFormState({
      name: view.name || '',
      description: view.description || '',
      statusesToExclude: (filters.statuses_to_exclude || []).map(String),
      resolutionActorsToExclude: (filters.resolution_actors_to_exclude || []).map(String),
      topics: (filters.topics || []).map(String),
      channelIds: (filters.channels || filters.channel_ids || []).map((c) => String(c?.id || c?._id || c)),
      employeeIds: (filters.employees || filters.employee_ids || []).map((e) => String(e?.id || e?._id || e))
    });
    setModalError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingView(null);
    setFormState(createEmptyFormState());
    setModalError(null);
  };

  const toggleSelection = (field, rawValue) => {
    const val = rawValue?.toString();
    if (!val) return;
    setFormState((prev) => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(val) ? current.filter((i) => i !== val) : [...current, val]
      };
    });
  };

  const handleSave = async () => {
    const name = formState.name.trim();
    if (!name) {
      setModalError(t('ticketViewsParameters.nameRequired', 'Le nom est requis.'));
      return;
    }
    setSubmitting(true);
    setModalError(null);
    const payload = {
      name,
      description: formState.description.trim() || null,
      filters: {
        statuses_to_exclude: formState.statusesToExclude,
        resolution_actors_to_exclude: formState.resolutionActorsToExclude,
        topics: formState.topics,
        channel_ids: formState.channelIds,
        employee_ids: formState.employeeIds,
        // The PUT replaces the whole filter set (TicketViewFilters): a
        // VIP-only view keeps that setting, which this window does not edit.
        ...(editingView?.filters?.is_vip_only ? { is_vip_only: true } : {})
      }
    };

    try {
      if (editingView) {
        await axios.put(`/api/tickets/ticket-views/${getViewId(editingView)}`, payload, { headers: authHeaders() });
        showFeedback('success', t('ticketViewsParameters.updateSuccess', 'Vue mise à jour.'));
      } else {
        await axios.post('/api/tickets/ticket-views', payload, { headers: authHeaders() });
        showFeedback('success', tl('views.createSuccess', 'Vue créée : elle apparaît maintenant comme onglet du tableau de bord.'));
      }
      closeModal();
      fetchTicketViews();
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      setModalError(
        err.response?.status === 409
          ? t('ticketViewsParameters.duplicateName', 'Une vue porte déjà ce nom.')
          : err.response?.data?.detail || tl('views.saveError', 'Échec de l’enregistrement de la vue.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (view) => {
    const viewId = getViewId(view);
    setDeletingIds((prev) => [...prev, viewId]);
    try {
      await axios.delete(`/api/tickets/ticket-views/${viewId}`, { headers: authHeaders() });
      setViews((prev) => (prev || []).filter((v) => getViewId(v) !== viewId));
      showFeedback('success', t('ticketViewsParameters.deleteSuccess', 'Vue supprimée.'));
    } catch (err) {
      if (err.response?.status === 401) { navigate('/'); return; }
      showFeedback('error', err.response?.data?.detail || t('ticketViewsParameters.deleteError', 'Échec de la suppression de la vue de tickets.'));
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== viewId));
      setToDelete(null);
    }
  };

  const openOnDashboard = (view) => {
    navigate(`/dashboard?activeTab=view:${getViewId(view)}`);
  };

  // ------------------------------------------------------------ summaries

  const getChannelName = (ref) => {
    if (typeof ref === 'object' && ref?.name) return ref.name;
    const ch = (options?.channels || []).find((c) => String(c.id || c._id) === String(ref));
    return ch?.name || t('ticketViewsParameters.unknownChannel', 'Canal inconnu');
  };

  const getEmployeeName = (ref) => {
    if (typeof ref === 'object' && ref?.name) return ref.name;
    const emp = (options?.employees || []).find((e) => String(e.id || e._id) === String(ref));
    return emp?.name || t('ticketViewsParameters.unknownEmployee', 'Employé inconnu');
  };

  // One segment per filter family: exclusions marked, restrictions plain.
  const filterSegments = (view) => {
    const filters = view.filters || {};
    const segments = [];
    const excludedStatuses = filters.statuses_to_exclude || [];
    const excludedActors = filters.resolution_actors_to_exclude || [];
    const topics = filters.topics || [];
    const channels = filters.channels || filters.channel_ids || [];
    const employees = filters.employees || filters.employee_ids || [];

    if (excludedStatuses.length > 0) {
      segments.push({
        key: 'statuses', exclude: true, icon: Ban,
        label: t('ticketViewsParameters.segHiddenStatuses', 'Masque'),
        value: excludedStatuses.map((s) => getStatusLabel(t, s)).join(', ')
      });
    }
    if (excludedActors.length > 0) {
      segments.push({
        key: 'actors', exclude: true, icon: Ban,
        label: t('ticketViewsParameters.segHiddenActors', 'Masque résolutions'),
        value: excludedActors.map((a) => t(`resolutionActors.${a}`, a)).join(', ')
      });
    }
    if (topics.length > 0) {
      segments.push({
        key: 'topics', icon: Tag,
        label: t('ticketViewsParameters.segTopics', 'Sujets'),
        value: topics.join(', ')
      });
    }
    if (channels.length > 0) {
      segments.push({
        key: 'channels', icon: Inbox,
        label: t('ticketViewsParameters.segChannels', 'Canaux'),
        value: channels.map(getChannelName).join(', ')
      });
    }
    if (employees.length > 0) {
      segments.push({
        key: 'employees', icon: UserRound,
        label: t('ticketViewsParameters.segEmployees', 'Employés'),
        value: employees.map(getEmployeeName).join(', ')
      });
    }
    // Set outside this page (API, migration): shown so it is never invisible.
    if (filters.is_vip_only) {
      segments.push({ key: 'vip', icon: Star, label: tl('views.segVip', 'Clients VIP uniquement'), value: null });
    }
    return segments;
  };

  // ------------------------------------------------------------ dialog fields

  const chipField = ({ id, label, field, items, getValue, getLabel, getColor, help }) => {
    const selected = formState[field] || [];
    return (
      <div className="u-field" role="group" aria-labelledby={id}>
        <div className="tvp-v2__field-head">
          <span className="u-label" id={id}>{label}</span>
          {selected.length > 0 && (
            <button
              type="button"
              className="u-btn u-btn--ghost u-btn--sm"
              onClick={() => setFormState((prev) => ({ ...prev, [field]: [] }))}
            >
              {t('ticketViewsParameters.clearSelection', 'Effacer la sélection')} ({selected.length})
            </button>
          )}
        </div>
        {options === null ? (
          <div className="tvp-v2__chips" aria-hidden="true">
            {[0, 1, 2].map((i) => <span key={i} className="u-skeleton tvp-v2__chip-skeleton" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="u-help">{t('ticketViewsParameters.noOptionsAvailable', 'Aucune option disponible')}</p>
        ) : (
          <div className="tvp-v2__chips">
            {items.map((item) => {
              const value = getValue(item);
              if (!value) return null;
              const isOn = selected.includes(value);
              const color = getColor ? getColor(item) : null;
              return (
                <button
                  key={`${field}-${value}`}
                  type="button"
                  className={`tvp-v2__chip${isOn ? ' is-on' : ''}`}
                  aria-pressed={isOn}
                  onClick={() => toggleSelection(field, value)}
                >
                  {/* The channel's own colour, chosen by the tenant (data, not design). */}
                  {color && <span className="tvp-v2__dot" style={{ backgroundColor: color }} aria-hidden="true" />}
                  {getLabel(item)}
                </button>
              );
            })}
          </div>
        )}
        {help && <p className="u-help">{help}</p>}
      </div>
    );
  };

  // ------------------------------------------------------------ rendering

  const loading = views === null;
  const list = orderedViews;
  const defaultView = list.find((view) => String(getViewId(view)) === String(defaultViewId));

  const subtitle = loading
    ? <span className="u-skeleton tvp-v2__line-skeleton" />
    : list.length === 0
      ? t('ticketViewsParameters.subtitle', 'Chaque vue devient un onglet du tableau de bord et filtre la liste des tickets.')
      : tl('views.subtitle', '{{value}} vues · chacune devient un onglet du tableau de bord et filtre la liste des tickets', {
        count: list.length, value: list.length,
      });

  return (
    <div className="u-page u-page--narrow tvp-v2">
      <PageHeader
        title={t('ticketViewsParameters.title', 'Vues de tickets')}
        subtitle={subtitle}
        actions={(
          <button type="button" className="u-btn u-btn--primary" onClick={openCreateModal}>
            <Plus aria-hidden="true" /> {t('ticketViewsParameters.create', 'Créer une vue')}
          </button>
        )}
      />

      <div className="u-well">
        {loading && (
          <div className="u-card tvp-v2__skeleton" aria-busy="true">
            {[0, 1, 2].map((i) => <span key={i} className="u-skeleton tvp-v2__row-skeleton" />)}
          </div>
        )}

        {!loading && list.length === 0 && (
          <div className="u-card">
            <EmptyState
              icon={Columns3}
              title={tl('views.emptyTitle', 'Aucune vue pour le moment')}
              action={(
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={openCreateModal}>
                  <Plus aria-hidden="true" /> {t('ticketViewsParameters.create', 'Créer une vue')}
                </button>
              )}
            >
              {tl('views.emptyHint', 'Créez-en une : elle apparaîtra comme onglet du tableau de bord, avec ses propres filtres.')}
            </EmptyState>
          </div>
        )}

        {!loading && list.length > 0 && (
          <>
            <Card
              icon={PanelsTopLeft}
              title={t('ticketViewsParameters.previewLabel', 'Aperçu des onglets du tableau de bord')}
              description={tl('views.previewDesc', 'Dans cet ordre, au-dessus de la liste des tickets. Un clic ouvre la vue.')}
            >
              <div className="u-tabs tvp-v2__preview" role="list">
                <span className="u-tab is-active" role="listitem">{t('general', 'Général')}</span>
                {list.map((view) => {
                  const isDefault = defaultView && getViewId(view) === getViewId(defaultView);
                  return (
                    <button
                      key={getViewId(view)}
                      type="button"
                      role="listitem"
                      className="u-tab"
                      title={t('ticketViewsParameters.openOnDashboard', 'Ouvrir sur le tableau de bord')}
                      onClick={() => openOnDashboard(view)}
                    >
                      {view.name}
                      {isDefault && <Star className="tvp-v2__default-star" aria-label={tl('views.yourDefault', 'Votre vue par défaut')} />}
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card
              icon={Columns3}
              title={tl('views.listTitle', 'Vues enregistrées')}
              badge={<Badge>{list.length}</Badge>}
              description={tl('views.listDesc', 'Les vues sont communes à toute l’équipe ; la vue par défaut se choisit dans « Affichage », sur le tableau de bord.')}
              flush
            >
              <ul className="u-list tvp-v2__list">
                {list.map((view) => {
                  const viewId = getViewId(view);
                  const segments = filterSegments(view);
                  const deleting = deletingIds.includes(viewId);
                  const created = formatDate(view.created_at);
                  const updated = formatDate(view.updated_at);
                  const isDefault = defaultView && viewId === getViewId(defaultView);
                  return (
                    <li key={viewId} className="u-row tvp-v2__row">
                      <div className="u-row__main">
                        <div className="u-row__title">
                          {view.name}
                          {isDefault && <Badge tone="accent" icon={Star}>{tl('views.yourDefault', 'Votre vue par défaut')}</Badge>}
                        </div>
                        {view.description && (
                          <div className="u-row__meta" title={view.description}>{view.description}</div>
                        )}
                        <div className="tvp-v2__filters">
                          {segments.length === 0 ? (
                            <span className="tvp-v2__none">{t('ticketViewsParameters.noFilters', 'Aucun filtre — tous les tickets')}</span>
                          ) : (
                            segments.map((seg) => {
                              const Icon = seg.icon;
                              return (
                                <span
                                  key={seg.key}
                                  className={`u-tag tvp-v2__seg${seg.exclude ? ' tvp-v2__seg--exclude' : ''}`}
                                  title={seg.value ? `${seg.label} : ${seg.value}` : seg.label}
                                >
                                  <Icon aria-hidden="true" />
                                  <b>{seg.label}</b>
                                  {seg.value && <span className="tvp-v2__seg-value">{seg.value}</span>}
                                </span>
                              );
                            })
                          )}
                        </div>
                        {(created || updated) && (
                          <div className="tvp-v2__dates">
                            {created && t('ticketViewsParameters.createdOn', 'Créée le {{date}}', { date: created })}
                            {created && updated && updated !== created && ' · '}
                            {updated && updated !== created && t('ticketViewsParameters.updatedOn', 'modifiée le {{date}}', { date: updated })}
                          </div>
                        )}
                      </div>
                      <div className="u-row__side">
                        <button
                          type="button"
                          className="u-btn u-btn--ghost u-btn--sm"
                          onClick={() => openOnDashboard(view)}
                          title={t('ticketViewsParameters.openOnDashboard', 'Ouvrir sur le tableau de bord')}
                        >
                          {tl('views.open', 'Ouvrir')}
                          <ExternalLink aria-hidden="true" />
                        </button>
                        <OverflowMenu
                          label={tl('views.more', 'Plus d’actions pour {{name}}', { name: view.name })}
                          items={[
                            { label: t('ticketViewsParameters.edit', 'Modifier'), icon: Pencil, onClick: () => openEditModal(view) },
                            { separator: true },
                            {
                              label: t('ticketViewsParameters.delete', 'Supprimer'),
                              icon: Trash2,
                              danger: true,
                              disabled: deleting,
                              onClick: () => setToDelete(view),
                            },
                          ]}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </>
        )}
      </div>

      {/* Create / edit dialog */}
      {showModal && (
        <Modal
          wide
          icon={Columns3}
          title={editingView
            ? t('ticketViewsParameters.editTitle', 'Modifier la vue')
            : t('ticketViewsParameters.createTitle', 'Créer une vue')}
          description={tl('views.modalDesc', 'Une vue filtre la liste du tableau de bord. Ce qui est masqué disparaît ; une restriction vide laisse tout passer.')}
          onClose={closeModal}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={closeModal} disabled={submitting}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={handleSave} disabled={submitting}>
                {submitting
                  ? t('ticketViewsParameters.submitting', 'Enregistrement…')
                  : editingView
                    ? t('common.save', 'Enregistrer')
                    : t('ticketViewsParameters.submit', 'Créer la vue')}
              </button>
            </>
          )}
        >
          <div className="u-form tvp-v2__form">
            {editingView?.filters?.is_vip_only && (
              <Banner tone="info" title={tl('views.vipTitle', 'Cette vue est limitée aux clients VIP')}>
                {t('listsPolishUi.views.vipKept', 'Ce réglage ne se modifie pas ici ; il est conservé à l’enregistrement.')}
              </Banner>
            )}

            <section className="tvp-v2__section">
              <h3 className="u-overline">{t('ticketViewsParameters.sectionIdentity', 'Identité')}</h3>
              <div className="u-field">
                <label className="u-label" htmlFor="tvp-name">{t('ticketViewsParameters.nameLabel', 'Nom')}</label>
                <input
                  id="tvp-name"
                  type="text"
                  className="u-input"
                  value={formState.name}
                  onChange={(e) => setFormState((f) => ({ ...f, name: e.target.value }))}
                  placeholder={t('ticketViewsParameters.namePlaceholder', 'Ex. : SAV urgent, Tickets Gmail…')}
                  aria-invalid={modalError && !formState.name.trim() ? 'true' : undefined}
                  autoFocus
                  required
                />
              </div>
              <div className="u-field">
                <label className="u-label" htmlFor="tvp-desc">{t('ticketViewsParameters.descriptionLabel', 'Description')}</label>
                <textarea
                  id="tvp-desc"
                  className="u-textarea tvp-v2__desc"
                  rows={2}
                  value={formState.description}
                  onChange={(e) => setFormState((f) => ({ ...f, description: e.target.value }))}
                  placeholder={t('ticketViewsParameters.descriptionPlaceholder', 'Description optionnelle')}
                />
              </div>
            </section>

            <section className="tvp-v2__section">
              <h3 className="u-overline">{t('ticketViewsParameters.sectionHide', 'Masquer')}</h3>
              <p className="u-help">{t('ticketViewsParameters.sectionHideHint', 'Les tickets correspondants n’apparaîtront pas dans cette vue.')}</p>
              {chipField({
                id: 'tvp-statuses',
                label: t('ticketViewsParameters.statusesLabel', 'Statuts à masquer'),
                field: 'statusesToExclude',
                items: options?.statuses || [],
                getValue: (s) => s?.toString() || '',
                getLabel: (s) => getStatusLabel(t, s),
              })}
              {chipField({
                id: 'tvp-actors',
                label: t('ticketViewsParameters.resolutionActorsLabel', 'Résolutions à masquer'),
                field: 'resolutionActorsToExclude',
                items: options?.resolutionActors || [],
                getValue: (a) => a?.toString() || '',
                getLabel: (a) => t(`resolutionActors.${a}`, a),
              })}
            </section>

            <section className="tvp-v2__section">
              <h3 className="u-overline">{t('ticketViewsParameters.sectionLimit', 'Limiter à')}</h3>
              <p className="u-help">{t('ticketViewsParameters.sectionLimitHint', 'Aucune sélection = tout est affiché.')}</p>
              {chipField({
                id: 'tvp-topics',
                label: t('ticketViewsParameters.topicsLabel', 'Sujets'),
                field: 'topics',
                items: options?.topics || [],
                getValue: (tp) => tp?.toString() || '',
                getLabel: (tp) => tp,
              })}
              {chipField({
                id: 'tvp-channels',
                label: t('ticketViewsParameters.channelsLabel', 'Canaux'),
                field: 'channelIds',
                items: options?.channels || [],
                getValue: (c) => c?.id?.toString() || c?._id?.toString() || '',
                getLabel: (c) => c?.name || t('ticketViewsParameters.unknownChannel', 'Canal inconnu'),
                getColor: (c) => c?.color || null,
              })}
              {chipField({
                id: 'tvp-employees',
                label: t('ticketViewsParameters.employeesLabel', 'Employés'),
                field: 'employeeIds',
                items: options?.employees || [],
                getValue: (e) => e?.id?.toString() || e?._id?.toString() || '',
                getLabel: (e) => e?.name || t('ticketViewsParameters.unknownEmployee', 'Employé inconnu'),
                help: tl('views.employeesNotApplied', 'Enregistré avec la vue, mais la liste des tickets ne filtre pas encore sur les employés.'),
              })}
            </section>

            {modalError && <Banner tone="danger">{modalError}</Banner>}
          </div>
        </Modal>
      )}

      {toDelete && (
        <ConfirmDialog
          title={tl('views.deleteTitle', 'Supprimer la vue « {{name}} » ?', { name: toDelete.name })}
          message={tl('views.deleteMessage', 'Son onglet disparaît du tableau de bord pour toute l’équipe. Les tickets ne sont pas modifiés.')}
          confirmLabel={t('ticketViewsParameters.delete', 'Supprimer')}
          cancelLabel={t('common.cancel', 'Annuler')}
          busy={deletingIds.includes(getViewId(toDelete))}
          onConfirm={() => handleDelete(toDelete)}
          onCancel={() => setToDelete(null)}
        />
      )}

      {feedback && (
        <Toast
          tone={feedback.type === 'success' ? 'success' : 'danger'}
          onClose={() => setFeedback(null)}
          closeLabel={t('common.close', 'Fermer')}
        >
          {feedback.text}
        </Toast>
      )}
    </div>
  );
};

export default TicketViewsParameters;
